import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    ActionButton,
    BackgroundToggleButton,
    DeviceSelect,
    DeviceRow,
    FieldGroup,
    IncomingCallAvatar,
    IncomingCallSummary,
    IncomingCallText,
    LobbyHeading,
    LobbyFooter,
    LobbyLayout,
    LobbyMain,
    LobbySide,
    LobbySubtitle,
    LobbyTitle,
    PreviewAvatar,
    PreviewControls,
    PreviewFallback,
    PreviewPanel,
    PreviewVideo,
    RoundControl,
    SecondaryButton,
    WindowBody
} from "src/scenes/Chat/components/ChatCallPanel.styled";
import BackgroundEffectsPicker from "./BackgroundEffectsPicker";
import {
    attachVideoStream,
    BACKGROUND_STYLES,
    deviceLabel,
    getInitials,
    getSelectedCustomBackgroundUrl,
    normalizeBackgroundEffect
} from "./ChatCallPanel.constants";
import MediaIcon from "./MediaIcon";
import { createVirtualBackgroundVideoTrack } from "./virtualBackgroundProcessor";

const ACTIVE_PREVIEW_DEVICE_PREFIX = "active-preview";
const CALL_VIDEO_CONSTRAINTS = {
    width: { ideal: 640, max: 640 },
    height: { ideal: 360, max: 360 },
    frameRate: { ideal: 15, max: 20 }
};

const normalizeDeviceIdForConstraints = deviceId => {
    if (!deviceId || deviceId === "default" || deviceId.startsWith(ACTIVE_PREVIEW_DEVICE_PREFIX)) {
        return "";
    }

    return deviceId;
};

const CallLobby = ({
    actionLabel = "Start Call",
    callerAvatarUrl = "",
    callerName = "",
    chat,
    currentChat,
    getUserDisplayName,
    isSendingCallAction,
    onCancel,
    onStart,
    showIncomingSummary = false
}) => {
    const [previewStream, setPreviewStream] = useState(null);
    const [processedPreviewStream, setProcessedPreviewStream] = useState(null);
    const [devices, setDevices] = useState([]);
    const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    const [microphoneDeviceId, setMicrophoneDeviceId] = useState(localStorage.getItem("meBusinessCallMicrophone") || "");
    const [cameraDeviceId, setCameraDeviceId] = useState(localStorage.getItem("meBusinessCallCamera") || "");
    const [speakerDeviceId, setSpeakerDeviceId] = useState(localStorage.getItem("meBusinessCallSpeaker") || "");
    const [backgroundEffect, setBackgroundEffect] = useState(normalizeBackgroundEffect(localStorage.getItem("meBusinessCallBackground") || "none"));
    const [backgroundPanelIsOpen, setBackgroundPanelIsOpen] = useState(false);
    const [backgroundEffectIsApplying, setBackgroundEffectIsApplying] = useState(false);
    const [backgroundEffectError, setBackgroundEffectError] = useState("");
    const [backgroundEffectAttempt, setBackgroundEffectAttempt] = useState(0);
    const [customBackgroundUrl, setCustomBackgroundUrl] = useState(getSelectedCustomBackgroundUrl);
    const [isStartingCall, setIsStartingCall] = useState(false);
    const [mediaError, setMediaError] = useState("");
    const [previewRetryCount, setPreviewRetryCount] = useState(0);
    const previewVideoRef = useRef(null);
    const backgroundProcessingPromiseRef = useRef(null);
    const previewStreamWasTransferredRef = useRef(false);
    const processedPreviewStreamWasTransferredRef = useRef(false);
    const lobbyChat = chat || currentChat;
    const conversationName = lobbyChat?.name || lobbyChat?.groupName || "this chat";

    const getPreviewTrackDevice = useCallback((kind, fallbackLabel) => {
        const track = kind === "audioinput"
            ? previewStream?.getAudioTracks?.()[0]
            : previewStream?.getVideoTracks?.()[0];
        const settings = track?.getSettings?.() || {};
        const label = track?.label || fallbackLabel;

        if (!track || !label) {
            return null;
        }

        return {
            deviceId: settings.deviceId || `${ACTIVE_PREVIEW_DEVICE_PREFIX}-${kind}`,
            groupId: settings.groupId || "",
            kind,
            label
        };
    }, [previewStream]);

    const mergeDevicesWithPreviewTrack = useCallback((deviceList, kind, fallbackLabel) => {
        const previewDevice = getPreviewTrackDevice(kind, fallbackLabel);

        if (!previewDevice) {
            return deviceList;
        }

        if (deviceList.some(device => device.deviceId === previewDevice.deviceId || device.label === previewDevice.label)) {
            return deviceList;
        }

        return [
            previewDevice,
            ...deviceList
        ];
    }, [getPreviewTrackDevice]);

    const loadDevices = useCallback(async () => {
        try {
            const nextDevices = await navigator.mediaDevices?.enumerateDevices?.();
            setDevices(nextDevices || []);
        } catch (error) {
            console.warn("Unable to enumerate call devices.", error);
        }
    }, []);

    useEffect(() => {
        loadDevices();
        navigator.mediaDevices?.addEventListener?.("devicechange", loadDevices);
        return () => {
            navigator.mediaDevices?.removeEventListener?.("devicechange", loadDevices);
        };
    }, [loadDevices]);

    const microphoneDevices = mergeDevicesWithPreviewTrack(
        devices.filter(device => device.kind === "audioinput"),
        "audioinput",
        "Current microphone"
    );
    const speakerDevices = devices.filter(device => device.kind === "audiooutput");
    const cameraDevices = mergeDevicesWithPreviewTrack(
        devices.filter(device => device.kind === "videoinput"),
        "videoinput",
        "Current camera"
    );
    const selectedMicrophoneDeviceId = microphoneDevices.some(device => device.deviceId === microphoneDeviceId)
        ? normalizeDeviceIdForConstraints(microphoneDeviceId)
        : "";
    const selectedCameraDeviceId = cameraDevices.some(device => device.deviceId === cameraDeviceId)
        ? normalizeDeviceIdForConstraints(cameraDeviceId)
        : "";
    const canSelectSpeaker = typeof HTMLMediaElement !== "undefined" &&
        Boolean(HTMLMediaElement.prototype.setSinkId);

    useEffect(() => {
        setMicrophoneDeviceId(localStorage.getItem("meBusinessCallMicrophone") || "");
        setCameraDeviceId(localStorage.getItem("meBusinessCallCamera") || "");
        setSpeakerDeviceId(localStorage.getItem("meBusinessCallSpeaker") || "");
        setBackgroundEffect(normalizeBackgroundEffect(localStorage.getItem("meBusinessCallBackground") || "none"));
        setCustomBackgroundUrl(getSelectedCustomBackgroundUrl());
        loadDevices();
    }, [actionLabel, loadDevices, lobbyChat?.uuid]);

    useEffect(() => {
        if (!devices.length) {
            return;
        }

        if (microphoneDeviceId && !microphoneDevices.some(device => device.deviceId === microphoneDeviceId)) {
            setMicrophoneDeviceId("");
        }

        if (cameraDeviceId && !cameraDevices.some(device => device.deviceId === cameraDeviceId)) {
            setCameraDeviceId("");
        }

        if (speakerDeviceId && !speakerDevices.some(device => device.deviceId === speakerDeviceId)) {
            setSpeakerDeviceId("");
        }
    }, [
        cameraDeviceId,
        cameraDevices,
        devices.length,
        microphoneDeviceId,
        microphoneDevices,
        speakerDeviceId,
        speakerDevices
    ]);

    useEffect(() => {
        if (devices.length > 0 && cameraDevices.length === 0) {
            setCameraEnabled(false);
            setMediaError("");
        }
    }, [cameraDevices.length, devices.length]);

    useEffect(() => {
        if (devices.length > 0 && microphoneDevices.length === 0) {
            setMicrophoneEnabled(false);
        }
    }, [devices.length, microphoneDevices.length]);

    useEffect(() => {
        const previewMicrophoneDevice = getPreviewTrackDevice("audioinput", "Current microphone");
        const previewCameraDevice = getPreviewTrackDevice("videoinput", "Current camera");

        if (!microphoneDeviceId && previewMicrophoneDevice?.deviceId?.startsWith(ACTIVE_PREVIEW_DEVICE_PREFIX)) {
            setMicrophoneDeviceId(previewMicrophoneDevice.deviceId);
        }

        if (!cameraDeviceId && previewCameraDevice?.deviceId?.startsWith(ACTIVE_PREVIEW_DEVICE_PREFIX)) {
            setCameraDeviceId(previewCameraDevice.deviceId);
        }
    }, [cameraDeviceId, getPreviewTrackDevice, microphoneDeviceId]);

    const getMediaErrorMessage = (error) => {
        if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
            if (cameraEnabled && microphoneEnabled) {
                return "Camera or microphone access is blocked. Allow access in your browser settings and try again.";
            }

            return cameraEnabled
                ? "Camera access is blocked. Allow access in your browser settings and try again."
                : "Microphone access is blocked. Allow access in your browser settings and try again.";
        }

        if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
            if (cameraEnabled && microphoneEnabled) {
                return "No camera or microphone was detected. Connect a device or start with camera and microphone off.";
            }

            return cameraEnabled
                ? "No camera was detected. Connect a camera or start with camera off."
                : "No microphone was detected. Connect a microphone or start muted.";
        }

        if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
            return "The selected camera or microphone is already in use. Choose another device or try again.";
        }

        return "Camera or microphone access is unavailable. Check browser permissions and try again.";
    };

    useEffect(() => {
        let cancelled = false;
        let stream = null;

        const startPreview = async () => {
            setMediaError("");
            if (!cameraEnabled && !microphoneEnabled) {
                setPreviewStream(null);
                loadDevices();
                return;
            }

            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: microphoneEnabled
                        ? selectedMicrophoneDeviceId ? { deviceId: { exact: selectedMicrophoneDeviceId } } : true
                        : false,
                    video: cameraEnabled
                        ? {
                            ...CALL_VIDEO_CONSTRAINTS,
                            ...(selectedCameraDeviceId ? { deviceId: { exact: selectedCameraDeviceId } } : {})
                        }
                        : false
                });
                if (!cancelled) {
                    setPreviewStream(stream);
                    loadDevices();
                }
            } catch (error) {
                console.warn("Unable to start call preview.", error);
                if (cameraEnabled && microphoneEnabled) {
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({
                            audio: selectedMicrophoneDeviceId ? { deviceId: { exact: selectedMicrophoneDeviceId } } : true,
                            video: false
                        });
                        if (!cancelled) {
                            setCameraEnabled(false);
                            setMediaError(getMediaErrorMessage(error));
                            setPreviewStream(stream);
                            loadDevices();
                        }
                        return;
                    } catch (audioError) {
                        console.warn("Unable to start audio-only call preview.", audioError);
                    }
                }
                if (!cancelled) {
                    setPreviewStream(null);
                    setMediaError(getMediaErrorMessage(error));
                }
            }
        };

        startPreview();
        return () => {
            cancelled = true;
            if (!previewStreamWasTransferredRef.current) {
                stream?.getTracks?.().forEach(track => track.stop());
            }
        };
    }, [
        cameraEnabled,
        microphoneEnabled,
        previewRetryCount,
        selectedCameraDeviceId,
        loadDevices,
        selectedMicrophoneDeviceId
    ]);

    useEffect(() => {
        let cancelled = false;
        let processedTrack = null;

        const processPreview = async () => {
            setProcessedPreviewStream(null);
            const [cameraTrack] = previewStream?.getVideoTracks?.() || [];
            if (!cameraTrack || backgroundEffect === "none") {
                if (!cancelled) {
                    setBackgroundEffectIsApplying(false);
                    setBackgroundEffectError("");
                }
                return null;
            }

            setBackgroundEffectIsApplying(true);
            setBackgroundEffectError("");
            try {
                processedTrack = await createVirtualBackgroundVideoTrack({
                    customBackgroundUrl,
                    effect: backgroundEffect,
                    sourceTrack: cameraTrack.clone()
                });
                if (cancelled && processedTrack) {
                    processedTrack.__meBusinessVirtualBackgroundCleanup?.();
                    processedTrack = null;
                    return null;
                }
                if (!cancelled && processedTrack) {
                    const nextProcessedPreviewStream = new MediaStream([
                        ...previewStream.getAudioTracks(),
                        processedTrack
                    ]);
                    setProcessedPreviewStream(nextProcessedPreviewStream);
                    setBackgroundEffectIsApplying(false);
                    return nextProcessedPreviewStream;
                }
            } catch (error) {
                console.warn("Unable to render virtual background preview.", error);
                if (!cancelled) {
                    setProcessedPreviewStream(null);
                    setBackgroundEffectIsApplying(false);
                    setBackgroundEffectError("The background effect could not be applied. Please try again.");
                }
            }

            return null;
        };

        const processingPromise = processPreview();
        backgroundProcessingPromiseRef.current = processingPromise;
        return () => {
            cancelled = true;
            if (backgroundProcessingPromiseRef.current === processingPromise) {
                backgroundProcessingPromiseRef.current = null;
            }
            if (!processedPreviewStreamWasTransferredRef.current) {
                processedTrack?.__meBusinessVirtualBackgroundCleanup?.();
                processedTrack?.stop?.();
            }
        };
    }, [backgroundEffect, backgroundEffectAttempt, customBackgroundUrl, previewStream]);

    useEffect(() => {
        attachVideoStream(previewVideoRef.current, processedPreviewStream || previewStream);
    }, [previewStream, processedPreviewStream]);

    const previewIdentityName = lobbyChat?.isGroupChat
        ? conversationName
        : getUserDisplayName(lobbyChat?.users?.find(user => user.matrixUserId !== currentChat?.matrixClient?.getUserId?.())?.matrixUserId, conversationName);
    const currentMatrixUserId = currentChat?.matrixClient?.getUserId?.() || lobbyChat?.matrixClient?.getUserId?.();
    const currentChatUser = lobbyChat?.users?.find(user => user.matrixUserId === currentMatrixUserId);
    const currentUserAvatarUrl = currentChatUser?.profilePictureUrl || currentChatUser?.employeeDetails?.profilePictureUrl || "";
    const lobbyTitleAction = actionLabel === "Join Call" ? "Join call with" : "Start call with";
    const incomingCallerName = callerName || conversationName;
    const previewVideoTrack = (processedPreviewStream || previewStream)
        ?.getVideoTracks?.()
        .find(track => !track.__meBusinessPlaceholder);
    const cameraIsActuallyEnabled = Boolean(
        cameraEnabled &&
        previewVideoTrack?.readyState === "live" &&
        previewVideoTrack.enabled !== false
    );

    const start = async () => {
        if (isSendingCallAction || isStartingCall) {
            return;
        }

        setIsStartingCall(true);
        const nextMicrophoneDeviceId = normalizeDeviceIdForConstraints(microphoneDeviceId);
        const nextCameraDeviceId = normalizeDeviceIdForConstraints(cameraDeviceId);

        localStorage.setItem("meBusinessCallMicrophone", nextMicrophoneDeviceId);
        localStorage.setItem("meBusinessCallCamera", nextCameraDeviceId);
        localStorage.setItem("meBusinessCallSpeaker", speakerDeviceId);
        const nextBackgroundEffect = normalizeBackgroundEffect(backgroundEffect);
        localStorage.setItem("meBusinessCallBackground", nextBackgroundEffect);
        const readyProcessedPreviewStream = processedPreviewStream
            || (nextBackgroundEffect !== "none"
                ? await backgroundProcessingPromiseRef.current
                : null);
        const hasProcessedPreview = nextBackgroundEffect !== "none" &&
            readyProcessedPreviewStream?.getVideoTracks?.().length > 0;
        const mediaStream = hasProcessedPreview ? readyProcessedPreviewStream : previewStream;

        if (hasProcessedPreview) {
            processedPreviewStreamWasTransferredRef.current = true;
            previewStream?.getVideoTracks?.().forEach(track => track.stop());
        }
        previewStreamWasTransferredRef.current = true;
        try {
            await onStart({
                microphoneEnabled,
                cameraEnabled: cameraIsActuallyEnabled,
                microphoneDeviceId: nextMicrophoneDeviceId,
                cameraDeviceId: nextCameraDeviceId,
                speakerDeviceId,
                backgroundEffect: nextBackgroundEffect,
                customBackgroundUrl,
                mediaStream
            });
        } finally {
            setIsStartingCall(false);
        }
    };

    const startingLabel = actionLabel === "Join Call" ? "Joining..." : "Starting...";
    const selectBackgroundEffect = (effect) => {
        const nextEffect = normalizeBackgroundEffect(effect);
        setBackgroundEffectError("");
        setBackgroundEffect(nextEffect);
        setBackgroundEffectAttempt(previous => previous + 1);
        if (nextEffect !== "none" && !cameraEnabled) {
            setCameraEnabled(true);
        }
    };

    return (
        <WindowBody $dedicated>
            <LobbyLayout>
                <LobbyMain>
                    <LobbyHeading>
                        {showIncomingSummary ? (
                            <IncomingCallSummary>
                                <IncomingCallAvatar $src={callerAvatarUrl}>
                                    {callerAvatarUrl ? "" : getInitials(incomingCallerName)}
                                </IncomingCallAvatar>
                                <IncomingCallText>
                                    <LobbyTitle>{incomingCallerName} is calling</LobbyTitle>
                                    <LobbySubtitle>{conversationName}</LobbySubtitle>
                                </IncomingCallText>
                            </IncomingCallSummary>
                        ) : (
                            <>
                                <LobbyTitle>{lobbyTitleAction} {conversationName}</LobbyTitle>
                                <LobbySubtitle>Preview your camera, choose devices, and decide whether to join with video.</LobbySubtitle>
                            </>
                        )}
                    </LobbyHeading>
                    <PreviewPanel $background={BACKGROUND_STYLES[backgroundEffect]}>
                        {cameraIsActuallyEnabled ? (
                            <PreviewVideo ref={previewVideoRef} autoPlay muted playsInline $effect={backgroundEffect} />
                        ) : (
                            <PreviewFallback>
                                <PreviewAvatar $src={currentUserAvatarUrl}>
                                    {currentUserAvatarUrl ? "" : getInitials(previewIdentityName)}
                                </PreviewAvatar>
                                <div>{mediaError || "Camera is off"}</div>
                                {mediaError &&
                                    <ActionButton type="button" onClick={() => setPreviewRetryCount(previous => previous + 1)}>
                                        Try Again
                                    </ActionButton>
                                }
                            </PreviewFallback>
                        )}
                        <PreviewControls aria-label="Pre-call media controls">
                            <RoundControl
                                type="button"
                                title={microphoneEnabled ? "Mute microphone" : "Unmute microphone"}
                                $active={microphoneEnabled}
                                onClick={() => setMicrophoneEnabled(previous => !previous)}
                            >
                                <MediaIcon type="mic" disabled={!microphoneEnabled} />
                            </RoundControl>
                            <RoundControl
                                type="button"
                                title={cameraEnabled ? "Turn camera off" : "Turn camera on"}
                                $active={cameraEnabled}
                                onClick={() => setCameraEnabled(previous => !previous)}
                            >
                                <MediaIcon type="video" disabled={!cameraEnabled} />
                            </RoundControl>
                        </PreviewControls>
                    </PreviewPanel>
                </LobbyMain>
                <LobbySide $topAligned={showIncomingSummary}>
                    <DeviceRow>
                        <FieldGroup>
                            Microphone
                            <DeviceSelect value={microphoneDeviceId} onChange={event => setMicrophoneDeviceId(event.target.value)}>
                                <option value="">Default microphone</option>
                                {microphoneDevices.map((device, index) => (
                                    <option key={device.deviceId} value={device.deviceId}>
                                        {deviceLabel(device, `Microphone ${index + 1}`)}
                                    </option>
                                ))}
                            </DeviceSelect>
                        </FieldGroup>
                        <FieldGroup>
                            Speaker
                            <DeviceSelect disabled={!canSelectSpeaker} value={speakerDeviceId} onChange={event => setSpeakerDeviceId(event.target.value)}>
                                <option value="">Default speaker</option>
                                {speakerDevices.map((device, index) => (
                                    <option key={device.deviceId} value={device.deviceId}>
                                        {deviceLabel(device, `Speaker ${index + 1}`)}
                                    </option>
                                ))}
                            </DeviceSelect>
                        </FieldGroup>
                        <FieldGroup>
                            Camera
                            <DeviceSelect value={cameraDeviceId} onChange={event => setCameraDeviceId(event.target.value)}>
                                <option value="">Default camera</option>
                                {cameraDevices.map((device, index) => (
                                    <option key={device.deviceId} value={device.deviceId}>
                                        {deviceLabel(device, `Camera ${index + 1}`)}
                                    </option>
                                ))}
                            </DeviceSelect>
                        </FieldGroup>
                    </DeviceRow>
                    <FieldGroup>
                        Background
                        <BackgroundToggleButton
                            type="button"
                            $open={backgroundPanelIsOpen}
                            onClick={() => setBackgroundPanelIsOpen(previous => !previous)}
                        >
                            Backgrounds &amp; Effects
                        </BackgroundToggleButton>
                    </FieldGroup>
                    {backgroundPanelIsOpen &&
                        <BackgroundEffectsPicker
                            customBackgroundUrl={customBackgroundUrl}
                            isApplying={backgroundEffectIsApplying}
                            onCustomBackgroundChange={setCustomBackgroundUrl}
                            onSelect={selectBackgroundEffect}
                            processingError={backgroundEffectError}
                            selectedEffect={backgroundEffect}
                        />
                    }
                    <LobbyFooter>
                        <ActionButton
                            type="button"
                            disabled={isSendingCallAction || isStartingCall || backgroundEffectIsApplying}
                            onClick={start}
                        >
                            {backgroundEffectIsApplying ? "Applying effect..." : isStartingCall ? startingLabel : actionLabel}
                        </ActionButton>
                        <SecondaryButton type="button" $inline disabled={isSendingCallAction || isStartingCall} onClick={onCancel}>
                            Cancel
                        </SecondaryButton>
                    </LobbyFooter>
                </LobbySide>
            </LobbyLayout>
        </WindowBody>
    );
};

export default CallLobby;
